import { ParticleContainer, Sprite } from 'pixi.js';
import { CutAway, TextureFactory } from '../core';
import { Point } from '../interface';

type NinimoPopData = {
  sprite: Sprite;
  position: Point;
  begin: {
    dt: number;
    rotation: number;
    scale: number;
  };
  end: {
    dt: number;
    rotation: number;
    scale: number;
  };
};

class StarCutAway extends CutAway {
  public particleContainer: ParticleContainer;
  public ninimoPops: Array<NinimoPopData>;
  public reverse: boolean = false;
  public normalize: number;

  constructor(normalize: number = 1) {
    super();
    this.ninimoPops = [];
    this.normalize = normalize;
    this.particleContainer = new ParticleContainer(1000, {
      uvs: false,
      position: true,
      scale: true,
      vertices: false,
      rotation: true,
      tint: false,
      alpha: false,
    });
    this.addChild(this.particleContainer);
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    if (this.value < 1) {
      for (const ninimoPop of this.ninimoPops) {
        if (this.value >= ninimoPop.end.dt) {
          ninimoPop.sprite.scale.set(ninimoPop.end.scale);
          ninimoPop.sprite.rotation = ninimoPop.end.rotation;
        } else if (this.value >= ninimoPop.begin.dt) {
          ninimoPop.sprite.scale.set(
            ninimoPop.begin.scale +
              ((ninimoPop.end.scale - ninimoPop.begin.scale) * (this.value - ninimoPop.begin.dt)) / 0.4
          );
          ninimoPop.sprite.rotation =
            ninimoPop.begin.rotation +
            ((ninimoPop.end.rotation - ninimoPop.begin.rotation) * (this.value - ninimoPop.begin.dt)) / 0.4;
        }
      }
    } else if (this.value > 1) {
      const reverseValue = 2 - this.value;

      for (const ninimoPop of this.ninimoPops) {
        if (reverseValue >= ninimoPop.end.dt) {
          ninimoPop.sprite.scale.set(ninimoPop.begin.scale);
          ninimoPop.sprite.rotation = ninimoPop.begin.rotation;
        } else if (reverseValue >= ninimoPop.begin.dt) {
          ninimoPop.sprite.scale.set(
            ninimoPop.begin.scale +
              ((ninimoPop.end.scale - ninimoPop.begin.scale) * (0.4 - (reverseValue - ninimoPop.begin.dt))) / 0.4
          );
          ninimoPop.sprite.rotation =
            ninimoPop.begin.rotation +
            ((ninimoPop.end.rotation - ninimoPop.begin.rotation) * (0.4 - (reverseValue - ninimoPop.begin.dt))) / 0.4;
        }
      }
    }
  }

  public setSize(width: number, height: number): void {
    super.setSize(width, height);

    const GRID_SIZE = 90 * this.normalize;
    for (let i = this.ninimoPops.length - 1; i >= 0; i--) {
      this.particleContainer.removeChild(this.ninimoPops[i].sprite);
      this.ninimoPops.splice(i, 1);
    }

    const ninimoWidth = Math.ceil(width / GRID_SIZE);
    const ninimoHeight = Math.ceil(height / GRID_SIZE);
    const texture = TextureFactory.instance.getSingleTexture('/textures/etc/star.png');

    for (let i = 0; i < ninimoHeight; i++) {
      for (let j = 0; j < ninimoWidth; j++) {
        let begin = ((ninimoWidth - j - Math.random() * 3) / ninimoWidth) * 0.6;
        if (begin < 0) begin = 0;
        if (this.reverse) {
          begin = 0.6 - begin;
        }

        const ninimoPop: NinimoPopData = {
          sprite: new Sprite(texture),
          position: {
            x: j * GRID_SIZE + GRID_SIZE / 2 + (Math.random() * GRID_SIZE) / 4 - (+Math.random() * GRID_SIZE) / 4,
            y: i * GRID_SIZE + GRID_SIZE / 2 + (Math.random() * GRID_SIZE) / 4 - (+Math.random() * GRID_SIZE) / 4,
          },
          begin: {
            dt: begin,
            scale: 0,
            rotation: Math.random() * Math.PI * 2,
          },
          end: {
            dt: begin + 0.4,
            scale: (0.9 + Math.random() * 1.125) * this.normalize,
            rotation: Math.random() * Math.PI * 2,
          },
        };

        ninimoPop.sprite.anchor.set(0.5);
        ninimoPop.sprite.position.x = ninimoPop.position.x;
        ninimoPop.sprite.position.y = ninimoPop.position.y;
        ninimoPop.sprite.rotation = ninimoPop.begin.rotation;
        ninimoPop.sprite.scale.set(ninimoPop.begin.scale);

        this.ninimoPops.push(ninimoPop);
        this.particleContainer.addChild(ninimoPop.sprite);
      }
    }
  }
}

export default StarCutAway;
